import React, {useEffect, useState} from 'react'
import {Box, Flex, Image, Text, useMultiStyleConfig} from '@chakra-ui/react'
import {CheckIcon} from '../../components/icons'
import {defineMessage, useIntl, FormattedMessage} from 'react-intl'
import {useCustomerType} from '@salesforce/commerce-sdk-react'
import {useCurrentCustomer} from '@salesforce/retail-react-app/app/hooks/use-current-customer'
import {useCurrentBasket} from '@salesforce/retail-react-app/app/hooks/use-current-basket'
import UserNewsletterBanner from '../../components/user-newsletter-banner'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'
import {useGoogleTagManager} from '../../custom-api/hooks/use-google-tag-manager'
import {registration_event} from '../../custom-api/layers/product/registration'
// TODO: Temporarily removed account completion block - Stack/RegisterForm no longer used
// import {Stack} from '@salesforce/retail-react-app/app/components/shared/ui'
// import RegisterForm from '../../components/register'
import {useLocation} from 'react-router'
import {getConfig} from '@salesforce/pwa-kit-runtime/utils/ssr-config'
import {Helmet} from 'react-helmet'

const ProductRegistrationConfirm = () => {
    const styles = useMultiStyleConfig('ProductRegistration', {variant: 'productSearch'})
    const confirmStyles = useMultiStyleConfig('ProductRegistrationConfirm')
    // TODO: Temporarily removed account completion block - stylesLogin no longer used
    // const stylesLogin = useMultiStyleConfig('Login')
    const {isRegistered, isGuest} = useCustomerType()
    const {data: customer} = useCurrentCustomer()
    const {data: basket} = useCurrentBasket()
    const [product, setProduct] = useState(null)
    const {locale} = useMultiSite()

    const location = useLocation()
    const {customParams} = getConfig()
    const currentPath = 'https://' + customParams.currentHost + location.pathname
    const intl = useIntl()
    const productRegistrationTitleMessage = defineMessage({
        id: 'product_registration.title',
        defaultMessage: 'Register your product',
    })

    const productRegistrationSubtitleMessage = defineMessage({
        id: 'product_registration.subtitle',
        defaultMessage: 'Register your product to unlock a world of benefits.',
    })
    const webPageSchemaMarkup = {
        '@context': 'https://schema.org/',
        '@type': 'WebPage',
        name: intl.formatMessage(productRegistrationTitleMessage),
        description: intl.formatMessage(productRegistrationSubtitleMessage),
        url: currentPath,
    }

    const imageObjectSchemaMarkup = {
        '@type': 'ImageObject',
        contentUrl: product?.image?.link || '',
        creditText: `De'Longhi Appliances S.r.l`,
    }

    useEffect(() => {
        const storedProduct = localStorage.getItem('selectedProduct')
        if (storedProduct) {
            setProduct(JSON.parse(storedProduct))
            localStorage.removeItem('selectedProduct')
        }
    }, [])

    const {addLayer} = useGoogleTagManager()
    useEffect(() => {
        if (product) {
            addLayer(
                registration_event.bind(this, {
                    event: 'productRegistration',
                    eventCategory: 'afterSales',
                    eventAction: 'productRegistration',
                    eventLabel: 'sent',
                    productRegistration: {
                        products: [
                            {
                                id: product.representedProduct?.id, // product SKU
                                name: product.c_defaultName, // product name (English)
                                category: product.c_subCategoryDefaultName, // product category (English)
                            },
                        ],
                    },
                }),
            )
        }
    }, [product])

    return (
        <Flex {...styles.container} layerStyle={'page'}>
            <Helmet>
                <script type="application/ld+json">
                    {JSON.stringify(webPageSchemaMarkup, null, 2)}
                </script>
                <script type="application/ld+json">
                    {JSON.stringify(imageObjectSchemaMarkup, null, 2)}
                </script>
            </Helmet>
            <Flex {...confirmStyles.header}>
                <Box {...confirmStyles.checkIconBox}>
                    <CheckIcon {...confirmStyles.checkIcon} />
                </Box>
                <Flex {...confirmStyles.headerInner}>
                    <Text {...confirmStyles.title}>
                        <FormattedMessage
                            id="product_registration_confirm.registration_completed"
                            defaultMessage="Registration completed"
                        />
                    </Text>
                    {!isGuest && isRegistered && (
                        <Text {...confirmStyles.subtitle}>
                            <FormattedMessage
                                id="product_registration_confirm.view_or_edit_registration"
                                defaultMessage="You can view or edit your product registration in the <u>My Account section</u>."
                                values={{
                                    u: (chunks) => (
                                        <u>
                                            <a
                                                href={`/${locale?.alias}/account`}
                                                rel="noopener noreferrer"
                                            >
                                                {chunks}
                                            </a>
                                        </u>
                                    ),
                                }}
                            />
                        </Text>
                    )}
                </Flex>
            </Flex>
            <Flex {...confirmStyles.detailsOuterContainer}>
                <Flex {...confirmStyles.imageContainer}>
                    <Image
                        {...confirmStyles.productImage}
                        src={product?.image?.link}
                        alt={product?.productName}
                    />
                </Flex>
                <Flex {...confirmStyles.detailsContainer}>
                    <Text {...confirmStyles.detailsTitle}>
                        <FormattedMessage
                            id="product_registration_confirm.congratulations"
                            defaultMessage="Congratulations {customerName}, you have just registered your product <b>{productName}!</b>"
                            values={{
                                b: (chunks) => <b>{chunks}</b>,
                                customerName: customer?.firstName || basket?.c_firstName,
                                productName: product?.productName,
                            }}
                        />
                    </Text>
                    <Text {...confirmStyles.detailsDescription}>
                        <FormattedMessage
                            id="product_registration_confirm.email_sent"
                            defaultMessage="We have sent an email with the details of your product registration.{br}{br}<b>Important</b>: if you have registered a product eligible for warranty extension, you will receive an additional confirmation email within 2-3 hours of registration."
                            values={{
                                br: <br />,
                                b: (chunks) => <b>{chunks}</b>,
                            }}
                        />
                    </Text>
                    <div className="del_digioh-trade_promo"></div>
                </Flex>
            </Flex>
            {/* TODO: SORT-11266 Temporarily removed account completion block - see request to remove from product registration thank you page
            {isGuest && basket?.customerInfo?.email && (
                <Stack __css={stylesLogin} width="100%">
                    <Flex className="guest-product-registration-confirm">
                        {basket?.customerInfo?.email && (
                            <RegisterForm
                                isThankYouPage={true}
                                email={basket?.customerInfo?.email}
                                isProductRegistrationPage={true}
                            />
                        )}
                    </Flex>
                </Stack>
            )} */}
            <UserNewsletterBanner
                description={
                    <FormattedMessage
                        id="product_registration_confirm.newsletter_description"
                        defaultMessage="Get tailored advice to get the most out of your product <b>{productName}</b>"
                        values={{
                            b: (chunks) => <b>{chunks}</b>,
                            productName: product?.productName,
                        }}
                    />
                }
            />
            {/* {isGuest && !isRegistered && (
                <Flex {...confirmStyles.newUser}>
                    <Flex></Flex>
                    <Flex {...confirmStyles.prosContainer}>
                        <Flex {...confirmStyles.labelsContainer}>
                            <Text {...confirmStyles.prosTitle}>
                                <FormattedMessage
                                    id="product_registration_confirm.benefits_title"
                                    defaultMessage="Your benefits by creating an account"
                                />
                            </Text>
                            <Text {...confirmStyles.prosDescription}>
                                <FormattedMessage
                                    id="product_registration_confirm.benefits_description"
                                    defaultMessage="Register now to enjoy the benefits of having an account."
                                />
                            </Text>
                        </Flex>
                        <Flex {...confirmStyles.iconsContainer}>
                            <Flex {...confirmStyles.iconLabel}>
                                <Icon as={SearchIcon} {...confirmStyles.proIcon} />
                                <Text {...confirmStyles.proDescription}>
                                    <FormattedMessage
                                        id="product_registration_confirm.track_orders"
                                        defaultMessage="Keep track of your orders and orders history"
                                    />
                                </Text>
                            </Flex>
                            <Flex {...confirmStyles.iconLabel}>
                                <Icon as={DiscountIcon} {...confirmStyles.proIcon} />
                                <Text {...confirmStyles.proDescription}>
                                    <FormattedMessage
                                        id="product_registration_confirm.exclusive_offers"
                                        defaultMessage="Exclusive offers with newsletter sign up"
                                    />
                                </Text>
                            </Flex>
                            <Flex {...confirmStyles.iconLabel}>
                                <Icon as={FavouriteIcon} {...confirmStyles.proIcon} />
                                <Text {...confirmStyles.proDescription}>
                                    <FormattedMessage
                                        id="product_registration_confirm.save_favourites"
                                        defaultMessage="Save your favourite products"
                                    />
                                </Text>
                            </Flex>
                            <Flex {...confirmStyles.iconLabel}>
                                <Icon as={ManualsIcon} {...confirmStyles.proIcon} />
                                <Text {...confirmStyles.proDescription}>
                                    <FormattedMessage
                                        id="product_registration_confirm.product_info"
                                        defaultMessage="Easy access to your product information and helpful tips"
                                    />
                                </Text>
                            </Flex>
                            <Flex {...confirmStyles.iconLabel}>
                                <Icon as={Icon360} {...confirmStyles.proIcon} />
                                <Text {...confirmStyles.proDescription}>
                                    <FormattedMessage
                                        id="product_registration_confirm.access_websites"
                                        defaultMessage="Access all De'Longhi Group websites with the same account"
                                    />
                                </Text>
                                <Box>
                                    <InfoIcon {...confirmStyles.infoIcon} />
                                </Box>
                            </Flex>
                        </Flex>
                    </Flex>
                </Flex>
            )} */}
        </Flex>
    )
}

export default ProductRegistrationConfirm
